import {
  RefObject,
  useEffect,
} from 'react';

/**
 * Moves focus to the first invalid field after browser validation fires.
 *
 * When a form is submitted with invalid required fields, the browser fires
 * `invalid` events on each field in quick succession. This hook listens for
 * those events in capture phase (since `invalid` does not bubble), debounces
 * until the burst is complete, then focuses the first field with
 * `aria-invalid="true"` so that screen readers (VoiceOver) announce the
 * field name and its associated error description.
 *
 * @param formRef - Ref attached to the `<form>` element.
 */
export const useFormValidationFocus = (formRef: RefObject<HTMLFormElement>): void => {
  useEffect(() => {
    const form = formRef.current;

    if (!form) {
      return undefined;
    }

    let timeout: ReturnType<typeof setTimeout>;

    const handleInvalid = () => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        const firstInvalid = form.querySelector('[aria-invalid="true"]') as HTMLElement | null;

        if (!firstInvalid) {
          return;
        }

        // Focus the associated error message rather than the invalid field
        // itself, so that screen readers announce the error without triggering
        // the field's onFocus handler (which would clear the error state).
        //
        // aria-describedby may list several ids — e.g. a field with on-demand
        // help text points at both its help description and its error message.
        // Pick the error id specifically; passing the whole multi-token string
        // to getElementById finds nothing and would fall back to focusing the
        // field, clearing the error we just surfaced.
        const describedBy = firstInvalid.getAttribute('aria-describedby');
        const describedByIds = describedBy ? describedBy.split(/\s+/).filter(Boolean) : [];
        const errorId = describedByIds.find(id => id.endsWith('-error')) ?? describedByIds[0];
        const errorEl = errorId ? document.getElementById(errorId) : null;
        const target: HTMLElement = errorEl ?? firstInvalid;

        if (!target.hasAttribute('tabindex')) {
          target.setAttribute('tabindex', '-1');
        }

        target.focus();
      }, 50);
    };

    form.addEventListener('invalid', handleInvalid, true);

    return () => {
      form.removeEventListener('invalid', handleInvalid, true);
      clearTimeout(timeout);
    };
  }, [formRef]);
};
